<template>
  <div class="tasks-page">
    <div class="tasks-content">
      <!-- 任务统计卡片 -->
      <div class="statistics-cards">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon total">
              <el-icon><Files /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ statistics.total }}</div>
              <div class="stat-label">全部任务</div>
            </div>
          </div>
        </el-card>
        
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon pending">
              <el-icon><Document /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ statistics.toProcess }}</div>
              <div class="stat-label">待处理</div>
            </div>
          </div>
        </el-card>
        
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon in-progress">
              <el-icon><User /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ statistics.inProgress }}</div>
              <div class="stat-label">处理中</div>
            </div>
          </div>
        </el-card>
        
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon completed">
              <el-icon><Document /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ statistics.completed }}</div>
              <div class="stat-label">已完成</div>
            </div>
          </div>
        </el-card>
        
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon failed">
              <el-icon><Document /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ statistics.failed || 0 }}</div>
              <div class="stat-label">失败</div>
            </div>
          </div>
        </el-card>
      </div>
      
      <!-- 筛选和排序工具栏 -->
      <div class="tasks-toolbar">
        <div class="toolbar-left">
          <span class="toolbar-label">状态筛选：</span>
          <el-select
            v-model="filterStatus"
            placeholder="全部状态"
            clearable
            style="width: 150px"
            @change="handleFilterChange"
          >
            <el-option label="全部" value="" />
            <el-option label="待分配" value="待分配" />
            <el-option label="待处理" value="待处理" />
            <el-option label="处理中" value="处理中" />
            <el-option label="已完成" value="已完成" />
            <el-option label="失败" value="失败" />
          </el-select>
        </div>
        <div class="toolbar-right">
          <span class="toolbar-label">排序方式：</span>
          <el-select
            v-model="sortField"
            placeholder="选择排序"
            style="width: 180px"
            @change="handleSortChange"
          >
            <el-option label="创建日期（降序）" value="createDate_desc" />
            <el-option label="创建日期（升序）" value="createDate_asc" />
            <el-option label="完成日期（降序）" value="completeDate_desc" />
            <el-option label="完成日期（升序）" value="completeDate_asc" />
            <el-option label="任务名称（升序）" value="taskName_asc" />
            <el-option label="任务名称（降序）" value="taskName_desc" />
          </el-select>
        </div>
      </div>
      
      <div class="tasks-list" v-loading="loading">
        <div v-if="filteredAndSortedTasks.length === 0" class="empty-tasks">
          <el-empty description="暂无任务数据" />
        </div>
        <div v-else class="task-card-grid">
          <el-card
            v-for="task in filteredAndSortedTasks"
            :key="task.id"
            class="task-card"
            shadow="hover"
            @click="handleTaskClick(task)"
          >
            <template #header>
              <div class="task-card-header">
                <h3 class="task-name">{{ task.taskName }}</h3>
                <el-tag :type="getStatusType(task.statusCode)" size="small">
                  {{ task.status }}
                </el-tag>
              </div>
            </template>
            
            <div class="task-card-body">
              <div class="task-info-item">
                <span class="task-info-label">任务描述：</span>
                <span class="task-info-value task-description">{{ task.description }}</span>
              </div>
              <div class="task-info-item">
                <span class="task-info-label">执行情况：</span>
                <span class="task-info-value task-description">{{ task.remark }}</span>
              </div>
              <div class="task-info-item">
                <span class="task-info-label">完成时间：</span>
                <span class="task-info-value">{{ task.completeDate ? formatDate(task.completeDate) : '未完成' }}</span>
              </div>
              
              <div class="task-info-item">
                <span class="task-info-label">执行人员：</span>
                <span class="task-info-value">{{ task.executor || '未分配' }}</span>
              </div>

              <div class="task-info-item2">
                <span class="task-info-label2">视频：</span>
                <span class="task-info-value">{{ task.totalTemplateQuantity || 0 }}条</span>
                <span class="task-info-label2" style="margin:0 6px;">|</span>
                <span class="task-info-label2">模板：</span>
                <span class="task-info-value">{{ task.templateCount || 0 }}个</span>
                <span class="task-info-label2" style="margin:0 6px;">|</span>
                <span class="task-info-label2">主角：</span>
                <span class="task-info-value">{{ task.characterCount || 0 }}人</span>
              </div>
              <div class="task-dates">
                <div class="task-date-item">
                  <span class="date-label">创建日期：</span>
                  <span class="date-value">{{ formatDate(task.createDate) }}</span>
                </div>
              </div>
            </div>
          </el-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Document, Files, User } from '@element-plus/icons-vue'
import { getMyTaskList, getMyTaskInfo, updateMyTaskStatus, getMyTaskStatistics } from '@/api/portal/pm'

const router = useRouter()

// 筛选和排序状态
const filterStatus = ref('')
const sortField = ref('createDate_desc')

// 任务列表
const taskList = ref([])
const loading = ref(false)

// 任务统计信息
const statistics = ref({
  total: 0,
  unassigned: 0,
  toProcess: 0,
  inProgress: 0,
  completed: 0,
  failed: 0
})

// 格式化日期
function formatDate(dateString) {
  if (!dateString) return '-'
  try {
    let date
    // 如果是数字（时间戳），直接转换
    if (typeof dateString === 'number') {
      date = new Date(dateString)
    } else if (typeof dateString === 'string') {
      // 尝试将字符串转换为数字（时间戳）
      const timestamp = Number(dateString)
      if (!isNaN(timestamp) && timestamp > 0) {
        // 是有效的时间戳字符串
        date = new Date(timestamp)
      } else {
        // 不是时间戳，尝试作为日期字符串解析
        const cleanDateString = dateString.replace('Z', '')
        date = new Date(cleanDateString)
      }
    } else {
      date = new Date(dateString)
    }
    
    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return '-'
    }
    
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    return '-'
  }
}

// 获取任务状态类型（根据statusCode）
function getStatusType(statusCode) {
  const statusMap = {
    1: 'warning',   // 待分配
    2: 'primary',   // 待处理
    3: 'warning',   // 处理中
    4: 'success',   // 已完成
    5: 'danger'    // 失败
  }
  return statusMap[statusCode] || 'info'
}

// 获取任务状态文本
function getStatusText(statusCode) {
  const statusMap = {
    1: '待分配',
    2: '待处理',
    3: '处理中',
    4: '已完成',
    5: '失败'
  }
  return statusMap[statusCode] || '未知'
}

// 状态文本转状态码
function getStatusCode(statusText) {
  const statusMap = {
    '待分配': 1,
    '待处理': 2,
    '处理中': 3,
    '已完成': 4,
    '失败': 5
  }
  return statusMap[statusText]
}

// 筛选和排序后的任务列表
const filteredAndSortedTasks = computed(() => {
  let result = [...taskList.value]
  
  // 状态筛选
  if (filterStatus.value) {
    result = result.filter(task => task.status === filterStatus.value)
  }
  
  // 排序
  if (sortField.value) {
    const [field, order] = sortField.value.split('_')
    result.sort((a, b) => {
      let aValue, bValue
      
      if (field === 'createDate' || field === 'completeDate') {
        // 日期排序
        aValue = a[field] ? new Date(a[field]).getTime() : 0
        bValue = b[field] ? new Date(b[field]).getTime() : 0
        // 对于没有完成日期的任务，放在最后
        if (field === 'completeDate') {
          if (!a[field] && !b[field]) return 0
          if (!a[field]) return 1
          if (!b[field]) return -1
        }
      } else if (field === 'taskName') {
        // 任务名称排序
        aValue = a[field] || ''
        bValue = b[field] || ''
      }
      
      if (order === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
      }
    })
  }
  
  return result
})

// 筛选变化处理
function handleFilterChange() {
  fetchTaskList()
}

// 排序变化处理
function handleSortChange() {
  // 排序逻辑在 computed 中处理
}

// 点击任务进入详情（在新页面打开）
function handleTaskClick(task) {
  // 在新页面打开任务详情
  const routeData = router.resolve({
    path: '/task/detail',
    query: {
      taskId: task.id
    }
  })
  // 使用window.open在新标签页打开
  window.open(routeData.href, '_self')
}

// 获取任务列表
async function fetchTaskList() {
  try {
    loading.value = true
    
    // 获取状态码
    let statusCode = null
    if (filterStatus.value) {
      statusCode = getStatusCode(filterStatus.value)
    }
    
    const res = await getMyTaskList(statusCode)
    if (res && res.code === 0) {
      taskList.value = (res.list || []).map(task => {
        // 解析taskDescription JSON字符串
        let taskDetails = {}
        if (task.taskDescription) {
          try {
            taskDetails = JSON.parse(task.taskDescription)
          } catch (e) {
            console.error('解析任务描述失败:', e)
          }
        }
        
        return {
          ...task,
          status: getStatusText(task.status),
          statusCode: task.status,
          description: taskDetails.description || taskDetails.prompt || '',
          executor: task.executorName || taskDetails.executorName || '未分配',  // 优先使用后端直接返回的 executorName
          copyCount: taskDetails.copyCount || 0,
          totalTemplateQuantity: taskDetails.totalTemplateQuantity|| 0,
          templateCount: taskDetails.templateCount || 0,
          characterCount: taskDetails.characterCount || 0,
          completeDate: task.endDate,
          // 保存原始详情数据
          copyResults: taskDetails.copyResults || [],
          templates: taskDetails.templates || [],
          selectedTemplateIds: taskDetails.selectedTemplateIds || [],
          selectedTemplateQuantities: taskDetails.selectedTemplateQuantities || {},
          characterDesc: taskDetails.characterDesc || '',
          materialLimit: taskDetails.materialLimit || 1,
          selectedVoices: taskDetails.selectedVoices || []
        }
      })
    }
  } catch (error) {
    console.error('获取任务列表失败:', error)
    ElMessage.error('获取任务列表失败')
  } finally {
    loading.value = false
  }
}

// 获取任务统计信息
async function fetchStatistics() {
  try {
    const res = await getMyTaskStatistics()
    if (res && res.code === 0) {
      statistics.value = res.statistics
    }
  } catch (error) {
    console.error('获取任务统计信息失败:', error)
  }
}

onMounted(() => {
  fetchTaskList()
  fetchStatistics()
})
</script>

<style scoped lang="scss">
// 任务页面 - 与 CardList.vue 风格统一
.tasks-page {
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  background: #fff;
  overflow: hidden;
}

.tasks-content {
  flex: 1;
  overflow-y: auto;
  padding: 30px 40px;
  width: 100%;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #E5E7EB;
    border-radius: 3px;
  }
}

// 页面标题栏
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .header-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 20px;
    box-shadow: 0 4px 12px rgba(var(--accent-primary-rgb), 0.3);
  }

  .header-info {
    .page-title {
      font-size: 20px;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0 0 2px;
      line-height: 1.3;
    }
    .page-subtitle {
      font-size: 13px;
      color: var(--text-secondary);
      margin: 0;
    }
  }
}

// 统计卡片
.statistics-cards {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.stat-card {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 130px;

  &:hover {
    border-color: var(--accent-blue, #4F46E5);
    transform: translateY(-5px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.06);
  }

  :deep(.el-card__body) {
    padding: 16px;
  }
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;

  &.total {
    background: rgba(79, 70, 229, 0.1);
    color: var(--accent-blue);
  }
  &.pending {
    background: rgba(144, 147, 153, 0.1);
    color: var(--text-secondary);
  }
  &.in-progress {
    background: rgba(245, 158, 11, 0.1);
    color: #F59E0B;
  }
  &.completed {
    background: rgba(16, 185, 129, 0.1);
    color: #10B981;
  }
  &.failed {
    background: rgba(239, 68, 68, 0.1);
    color: #EF4444;
  }
}

.stat-info {
  flex: 1;

  .stat-value {
    font-size: 24px;
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1;
    margin-bottom: 4px;
  }

  .stat-label {
    font-size: 12px;
    color: var(--text-secondary);
    font-weight: 500;
  }
}

// 工具栏
.tasks-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  margin-bottom: 16px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #E5E7EB;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-label {
  color: var(--text-secondary);
  font-size: 13px;
  white-space: nowrap;
}

// 空状态
.empty-tasks {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  padding: 40px;
  color: var(--text-secondary);

  :deep(.el-empty__description p) {
    color: var(--text-secondary);
  }
}

// 任务卡片网格
.task-card-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  padding: 4px 0 16px;
}

// 任务卡片
.task-card {
  /* 品牌渐变底色（通过伪元素+半透明 header/body 透出来） */
  background: rgba(255, 255, 255, 0.65);
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 355px;
  /* 保险：强制裁剪，避免 ElementPlus 内部溢出导致圆角被“涂出” */
  overflow: hidden !important;
  position: relative;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  &::before {
    content: '';
    position: absolute;
    /* 避免覆盖 border 描边区域 */
    inset: 0;
    border-radius: inherit;
    background:
      radial-gradient(500px 160px at 18% 0%, rgba(79, 70, 229, 0.22), transparent 60%),
      radial-gradient(420px 160px at 88% 20%, rgba(147, 51, 234, 0.18), transparent 55%),
      linear-gradient(135deg, rgba(79, 70, 229, 0.10) 0%, rgba(147, 51, 234, 0.10) 100%);
    opacity: 1;
    z-index: 0;
    pointer-events: none;
  }

  /* 确保内容层在渐变底纹之上 */
  :deep(.el-card__header),
  :deep(.el-card__body) {
    position: relative;
    z-index: 1;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.12);
    border-color: var(--accent-blue, #4F46E5);
  }

  :deep(.el-card__header) {
    background: rgba(249, 250, 251, 0.72);
    border-bottom: 1px solid rgba(229, 231, 235, 0.95);
    padding: 14px 16px;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }

  :deep(.el-card__body) {
    padding: 16px;
    background: rgba(255, 255, 255, 0.82);
    /* 避免 blur 的视觉溢出到圆角外 */
    backdrop-filter: blur(6px);
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  }
}

.task-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.task-name {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-card-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.task-info-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
}

.task-info-item2 {
  display: flex;
  align-items: center;
  font-size: 13px;
  gap: 4px;
  flex-wrap: wrap;
}

.task-info-label {
  color: var(--text-secondary);
  font-size: 13px;
  min-width: 60px;
  flex-shrink: 0;
  opacity: 0.8;
}

.task-info-label2 {
  color: var(--text-secondary);
  font-size: 13px;
  opacity: 0.8;
}

.task-info-value {
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 500;
}

.task-info-value.task-description {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
}

.task-dates {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid #E5E7EB;
}

.task-date-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.date-label {
  color: var(--text-secondary);
  opacity: 0.7;
  min-width: 60px;
  flex-shrink: 0;
}

.date-value {
  color: var(--text-secondary);
  font-family: 'Consolas', monospace;
}

@media (max-width: 768px) {
  .tasks-toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .toolbar-left,
  .toolbar-right {
    width: 100%;
    justify-content: space-between;
  }

  .tasks-toolbar :deep(.el-select) {
    flex: 1;
  }

  .task-card {
    width: 100%;
    max-width: 400px;
  }

  .task-card-grid {
    justify-content: center;
  }

  .statistics-cards {
    gap: 8px;
  }
}
</style>
